<script setup lang="ts">
import { api } from '@/api';
import {
  DueState,
  JobState,
  JobOccurrenceCatalogDto,
  JobOccurrenceFilterOptionsDto,
} from '@/api/generator';
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useLoading } from '@/utils/useLoading';
import { useTranslation } from 'i18next-vue';
import { JobOccurrencesFilterObject } from './types';
import JobOccurrencesOverviewFilter from './components/JobOccurrencesOverviewFilter.vue';
import { buildOdataQuery } from '@/utils/odata';
import { ActiveTabType, TabDefinition } from '@/components/Tabs/types';
import { useTabs } from '@/components/Tabs/useTabs';
import { useRoute, useRouter } from 'vue-router';
import JobOccurrenceTable, {
  JobOccurrenceTableProps,
} from '@/views/jobOccurrences/components/JobOccurrenceTable/JobOccurrenceTable.vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  ServerSidePaginationOptions,
  useServerSideOdataPagination,
} from '@/utils/useServerSideOdataPagination';
import Tabs from '@/components/Tabs/Tabs.vue';
import { getUrlComposition } from '@/utils/url';
import {
  deleteJobOccurrence,
  getJobOccurrenceContextMenuActions,
} from './utils/jobOccurrenceActions';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { useEntityEventStream } from './utils/useEntityEventHub';
import { mapTableToDtoKeys } from './components/JobOccurrenceTable/jobOccurrenceTable';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';
import debounce from 'lodash/debounce';
import { useLatestApiRequest } from './utils/useLatestApiRequest';

const route = useRoute();

const { onEntityEvent } = useEntityEventStream();

const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const { key: routeKey } = getUrlComposition(route.fullPath);

const { t } = useTranslation();

const router = useRouter();

const tabs = computed(
  () =>
    ({
      open: { title: t('JobGroup.open'), hideContentSheet: true },
      review: { title: t('JobGroup.review'), hideContentSheet: true },
      completed: { title: t('JobGroup.completed'), hideContentSheet: true },
    }) satisfies TabDefinition,
);

const { activeTab, selectByKey } = useTabs({
  tabs,
  initialTab: routeKey as ActiveTabType,
});

const tableOptions = computed<Partial<JobOccurrenceTableProps>>(() => {
  switch (activeTab.value) {
    case 'open':
      return {
        columns: [
          'jobConfigurationName',
          'targetSystem.name',
          'frequency',
          'period',
          'assigned',
          'review',
          'state',
          'progress',
          'contextMenu',
        ],
        defaultSorting: [{ key: 'period', order: 'asc' }],
      };
    case 'review':
      return {
        columns: [
          'jobConfigurationName',
          'targetSystem.name',
          'frequency',
          'period',
          'assigned',
          'review',
          'state',
          'progress',
          'contextMenu',
        ],
        defaultSorting: [{ key: 'state', order: 'asc' }],
      };
    case 'completed':
    default:
      return {
        columns: [
          'jobConfigurationName',
          'targetSystem.name',
          'frequency',
          'period',
          'assigned',
          'review',
          'completedByUser.name',
          'progress',
          'approvedBy',
          'contextMenu',
        ],
        defaultSorting: [{ key: 'completedByUser.name', order: 'desc' }],
      };
  }
});

const jobOccurrences = ref<JobOccurrenceCatalogDto[]>([]);

const filterObject = ref<JobOccurrencesFilterObject>({});
const filterOptions = ref<JobOccurrenceFilterOptionsDto>();

const tabFilter = computed(() => {
  const tab = activeTab.value as ActiveTabType;

  const finishedStates = [JobState.Completed, JobState.Approved];

  const reviewStates = [JobState.ReviewPending, JobState.InReview];

  return {
    and: [
      ...(tab === 'open'
        ? [
            {
              not: {
                state: {
                  in: [...finishedStates, ...reviewStates],
                },
              },
            },
          ]
        : []),
      ...(tab === 'completed'
        ? [
            {
              state: {
                in: finishedStates,
              },
            },
          ]
        : []),
      ...(tab === 'review'
        ? [
            {
              state: {
                in: reviewStates,
              },
            },
          ]
        : []),
    ],
  };
});

const { action: deleteAction } = useActionWithConfirmation({
  confirmedAction: async (jobOccurrence: JobOccurrenceCatalogDto) => {
    const success = await deleteJobOccurrence({
      ...jobOccurrence,
    });
    if (success) {
      await loadJobOccurrences();
    }
  },
});

function getContextMenuActions(item: JobOccurrenceCatalogDto): ContextMenuAction[] {
  return getJobOccurrenceContextMenuActions(item, router, t, deleteAction);
}

const filter = computed(() => ({
  reviewRequired: { in: filterObject.value.reviewRequired },
  frequency: { in: filterObject.value.frequencies },
  state: { in: filterObject.value.states },
  targetSystemId: { in: filterObject.value.targetSystemIds },
  ...(filterObject.value.startDateTime || filterObject.value.endDateTime
    ? {
        or: [
          {
            start: {
              ge: filterObject.value.startDateTime
                ? { type: 'raw', value: filterObject.value.startDateTime }
                : undefined,
              le: filterObject.value.endDateTime
                ? { type: 'raw', value: filterObject.value.endDateTime }
                : undefined,
            },
          },
          {
            dueState: DueState.Overdue,
          },
        ],
      }
    : {}),
}));

const itemsLength = ref<number>();
const jobOccurrencesRequest = useLatestApiRequest({
  request: api.jobOccurrencesApi.getJobOccurrences.bind(api.jobOccurrencesApi),
});
const filterOptionsRequest = useLatestApiRequest({
  request: api.jobOccurrencesApi.getFilterOptions.bind(api.jobOccurrencesApi),
});

const {
  action: loadJobOccurrences,
  actionWithoutLoading: silentUpdate,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result, success } = await jobOccurrencesRequest.call(
    buildOdataQuery({
      ...paginationQuery.value,
      search: filterObject.value.search,
      filter: {
        ...filter.value,
        ...tabFilter.value,
      },
    }),
  );

  if (!success) {
    return jobOccurrences.value;
  }

  jobOccurrences.value = result?.value ?? [];
  itemsLength.value = result?.odataCount;

  return jobOccurrences.value;
}, true);

onEntityEvent(silentUpdate);

const {
  query: paginationQuery,
  updateQuery: updatePaginationQuery,
  onUpdateQuery: onUpdatePaginationQuery,
} = useServerSideOdataPagination();

onUpdatePaginationQuery(async () => {
  await loadJobOccurrences();
});

const tablePageInitialDefaults: ServerSidePaginationOptions = {
  page: 1,
  itemsPerPage: 20,
  sortBy: mapTableToDtoKeys(tableOptions.value.defaultSorting),
};

function resetFilter() {
  filterObject.value = {};
}

async function updateFilterOptions() {
  const { result, success } = await filterOptionsRequest.call(
    buildOdataQuery({ filter: tabFilter.value }),
  );
  if (!success) {
    return;
  }

  const nextFilterOptions = result?.value?.[0] ?? undefined;
  if (nextFilterOptions) {
    filterOptions.value = nextFilterOptions;
  }
}

const updateDataByFilter = debounce(async () => {
  await loadJobOccurrences();
}, 350);

watch(
  () => activeTab.value as ActiveTabType,
  async () => {
    updateDataByFilter.cancel();
    resetFilter();
    await updateFilterOptions();
    await loadJobOccurrences();
  },
);

watch(
  () => filter.value,
  () => {
    updateDataByFilter();
  },
);

onMounted(async () => {
  await updateFilterOptions();

  updatePaginationQuery(tablePageInitialDefaults);
});

onBeforeUnmount(() => {
  updateDataByFilter.cancel();
});
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('JobOccurrence_other')" />
    </template>

    <v-toolbar class="pageTabs bg-primary-lighten-5 mt-2" height="48">
      <Tabs
        :modelValue="activeTab"
        :tabs="tabs"
        withShadow
        class="w-100"
        @update:modelValue="selectByKey($event as any)"
      />
    </v-toolbar>

    <JobOccurrencesOverviewFilter
      v-model="filterObject"
      :filterOptions="filterOptions"
      :activeTab="activeTab"
    />

    <div ref="table">
      <JobOccurrenceTable
        :jobOccurrences="jobOccurrences"
        :columns="tableOptions.columns"
        :defaultSorting="tableOptions.defaultSorting"
        :serverSidePagination="true"
        :loading="loading"
        :itemsLength="itemsLength ?? 0"
        :activeTab="activeTab"
        :hideDueBadge="activeTab === 'completed' || activeTab === 'review'"
        :showPeriodTime="activeTab === 'open'"
        :height="height"
        fixedHeader
        @update:options="updatePaginationQuery"
      >
        <template #actions="{ item }">
          <ContextMenu
            :actions="getContextMenuActions(item)"
            :closeOnScrollElements="tableScrollElement"
          />
        </template>
      </JobOccurrenceTable>
    </div>
  </BasePage>
</template>
